<template lang="pug">
  div(class="m-block-premium-features-stepper")
    v-stepper(
      v-model="step"
      flat
    )
      v-stepper-items
        v-stepper-content(
          v-if="!isTrialActivated"
          :step="1"
          class="m-block-premium-features-stepper__stepper-content"
        )
          onboarding-features-intro-block(:features="features")
        v-stepper-content(
          v-for="(step, idx) in steps"
          :key="idx"
          :step="countStepIndex(idx)"
          class="m-block-premium-features-stepper__stepper-content"
        )
          div(class="m-block-premium-features-stepper__image")
            div(class="m-block-premium-features-stepper__step-chip text-10px dark") {{ step.name }}
            v-img(
              :src="step.image"
              max-width="327"
            )
          div(class="m-block-premium-features-stepper__content")
            div(
              v-for="(feature, idx) in step.features"
              :key="idx"
              class="m-block-premium-features-stepper__item"
            )
              div(class="m-block-premium-features-stepper__item-icon")
                img(:src="feature.icon")
              div
                div(class="text-14px dark mb-1") {{ feature.title }}
                div(class="text-12px fw-300 m-block-premium-features-stepper__item-description") {{ feature.description }}
        v-stepper-content(
          :step="lastStepIndex"
          class="m-block-premium-features-stepper__stepper-content"
        )
          onboarding-features-outro-block(:features="features")
    div(
      class="m-block-premium-features-stepper__footer"
      :class="{ 'justify-end': step === stepsMap.intro && !isTrialActivated }"
    )
      template(v-if="step === stepsMap.intro && !isTrialActivated")
        div(class="m-block-premium-features-stepper__actions")
          v-btn(
            class="main-button"
            @click="handleIntroStep"
          ) {{ introButtonText }}
      template(v-else)
        div(class="m-block-premium-features-stepper__footer-left")
          v-btn(
            icon
            @click="goBack"
          )
            e-svg-icon arrow-narrow-left
          div(class="m-block-premium-features-stepper__progress-bar-wrapper")
            v-progress-linear(
              :value="progress"
              rounded
              color="#8F257D"
              background-color="#121212"
              background-opacity="0.06"
              height="6"
            )
            div(class="text-12px fw-300") {{ progress }}%
        div(
          v-if="step === stepsMap.outro"
          class="m-block-premium-features-stepper__actions"
        )
          v-btn(
            class="main-button"
            :loading="loading"
            @click="handleOutroStep"
          ) {{ outroButtonText }}
        div(
          v-else
          class="m-block-premium-features-stepper__actions"
        )
          v-btn(
            v-if="!isTrialActivated"
            class="secondary-button"
            :loading="loading"
            @click="tryPremium"
          ) {{ $t('Try') }}
          v-btn(
            class="main-button"
            :disabled="loading"
            @click="goNext"
          ) {{ $t('Continue') }}
</template>

<script>
import { mapGetters } from 'vuex'
import OnboardingFeaturesIntroBlock from '~/modules/onboarding/components/onboarding-features-intro-block.vue'
import OnboardingFeaturesOutroBlock from '~/modules/onboarding/components/onboarding-features-outro-block.vue'

const OUTRO_STEP = 1
const INTRO_AND_OUTRO_STEPS = 2

export default {
  name: 'MBlockPremiumFeaturesStepper',
  components: {
    OnboardingFeaturesIntroBlock,
    OnboardingFeaturesOutroBlock
  },
  props: {
    closeModal: {
      type: Function,
      default: () => {}
    },
    isTrialActivated: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      step: 1
    }
  },
  computed: {
    ...mapGetters('organizations', {
      organizations: 'items'
    }),
    percent() {
      const stepsProgressPercentages = [10, 30, 50, 70, 90, 100]
      const startStep = this.isTrialActivated ? 1 : 2
      const INTRO_STEP_KEY = 1
      const initialObject = this.isTrialActivated ? {} : { [INTRO_STEP_KEY]: 0 }

      return stepsProgressPercentages.reduce(
        (acc, value, index) => ({ ...acc, [index + startStep]: value }),
        initialObject
      )
    },
    progress() {
      return this.percent[this.step] ?? 0
    },
    features() {
      return [
        '100 вихідних документів на місяць',
        'Погодження документів',
        'Структурована система папок',
        'Готові шаблони для швидкого старту',
        'База контрагентів для швидкої роботи',
        'Віджет документу до вашого сервісу',
        'Коментування та обговорення',
        'API для інтеграції з вашими системами'
      ]
    },
    steps() {
      return [
        {
          image: require('~/assets/images/premium/document-create-screen.png'),
          name: this.$t('Participants route'),
          features: [
            {
              icon: require('~/assets/images/writing-hand.png'),
              title: this.$t('Configure route actions'),
              description: this.$t(
                'Ask who should review, approve, or sign a document - in the right order'
              )
            },
            {
              icon: require('~/assets/images/rocket.png'),
              title: this.$t('Run routes for any processes'),
              description: this.$t(
                'Suitable for vacation requests, orders, approvals with the manager, accounting, lawyer, etc'
              )
            },
            {
              icon: require('~/assets/images/handshake.png'),
              title: this.$t('One document - multiple signatories'),
              description: this.$t(
                'The document goes through all stages of approval automatically, without manual forwarding'
              )
            }
          ]
        },
        {
          image: require('~/assets/images/premium/document-participants-screen.png'),
          name: this.$t('Participants route'),
          features: [
            {
              icon: require('~/assets/images/compass.png'),
              title: this.$t('Transparency at every stage'),
              description: this.$t(
                'Track who has already approved a document and who hasn`t - all in one window'
              )
            },
            {
              icon: require('~/assets/images/bell.png'),
              title: this.$t('Automatic statuses and reminders'),
              description: this.$t(
                'Each participant sees only their own step. Reminders come automatically, without your intervention'
              )
            },
            {
              icon: require('~/assets/images/message-pen.png'),
              title: this.$t('Ability to react route'),
              description: this.$t(
                'If necessary, add/change route participants or their target action'
              )
            }
          ]
        },
        {
          image: require('~/assets/images/premium/document-comments-screen.png'),
          name: this.$t('Commenting'),
          features: [
            {
              icon: require('~/assets/images/speech-baloon.png'),
              title: this.$t('Comment documents'),
              description: this.$t(
                'All discussions stay next to the document - never get lost in the correspondence'
              )
            },
            {
              icon: require('~/assets/images/talking-head.png'),
              title: this.$t('Comments from all participants'),
              description: this.$t(
                'Employees, managers, accountants can leave their questions and clarifications in a convenient format'
              )
            },
            {
              icon: require('~/assets/images/email.png'),
              title: this.$t('Fewer unnecessary meetings and letters'),
              description: this.$t(
                'Resolve everything on the document at once, without having to write separate letters or make calls'
              )
            }
          ]
        },
        {
          image: require('~/assets/images/premium/documents-create-folder-screen.png'),
          name: this.$t('Folders'),
          features: [
            {
              icon: require('~/assets/images/folder.png'),
              title: this.$t('Folders, like in a physical archive'),
              description: this.$t(
                'Create your own structure of convenient folders - by employees, document types or periods'
              )
            },
            {
              icon: require('~/assets/images/search.png'),
              title: this.$t('Fast search'),
              description: this.$t(
                'Find the document you need in seconds thanks to filters, tags and search'
              )
            },
            {
              icon: require('~/assets/images/package-box.png'),
              title: this.$t('Organized storage'),
              description: this.$t(
                'After signing, documents are stored in the archive - you always know where to find them'
              )
            }
          ]
        },
        {
          image: require('~/assets/images/premium/contacts-screen.png'),
          name: this.$t('Contacts'),
          features: [
            {
              icon: require('~/assets/images/card-index.png'),
              title: this.$t('Create own base of contacts'),
              description: this.$t(
                'Save the full name, email and EDRPOU of counterparties so that you don`t have to re-enter them every time'
              )
            },
            {
              icon: require('~/assets/images/inbox-box.png'),
              title: this.$t('Send documents in few clicks'),
              description: this.$t(
                'When creating a document, the contact will be pulled up automatically - without searching in other systems'
              )
            },
            {
              icon: require('~/assets/images/brain.png'),
              title: this.$t('Remember instead you'),
              description: this.$t(
                'All the people you know are already in the system - just select and send'
              )
            }
          ]
        }
      ]
    },
    stepsMap() {
      const stepKeys = [
        'documentCreate',
        'documentParticipants',
        'documentComments',
        'createFolder',
        'contactsTable',
        'outro'
      ]

      const startStepIndex = this.isTrialActivated ? 1 : 2
      const initialObject = this.isTrialActivated ? {} : { intro: 1 }

      return stepKeys.reduce(
        (acc, key, index) => ({ ...acc, [key]: index + startStepIndex }),
        initialObject
      )
    },
    introButtonText() {
      if (!this.organizations.length) {
        return this.$t('Add organization')
      } else if (!this.$Organization) {
        return this.$t('Select organization')
      } else {
        return this.$t('How it works')
      }
    },
    outroButtonText() {
      return this.isTrialActivated ? this.$t('Understand') : this.$t('Activate access')
    },
    lastStepIndex() {
      // Якщо trial Premium активований, то пропускаємо показ першого кроку, показуємо відразу фічі та фінальний крок
      return this.isTrialActivated
        ? this.steps.length + OUTRO_STEP
        : this.steps.length + INTRO_AND_OUTRO_STEPS
    }
  },
  methods: {
    goBack() {
      if (this.step > 1) {
        this.step--
      }
    },
    goNext() {
      const maxStep = this.isTrialActivated ? this.steps.length + 1 : this.steps.length + 2 // 2 = intro step and final step
      if (this.step < maxStep) {
        this.step++
      }
    },
    handleIntroStep() {
      if (!this.organizations.length) {
        this.closeModal(true)
        this.$router.push({ name: 'my-organizations' })
      } else if (!this.$Organization) {
        this.closeModal(true)
        this.$store.commit('profilemenu/SET_IS_OPEN', true)
      } else {
        this.goNext()
      }
    },
    async handleOutroStep() {
      if (this.isTrialActivated) {
        this.closeModal(true)
      } else {
        await this.tryPremium()
      }
    },
    async tryPremium() {
      try {
        this.loading = true
        await this.$store.dispatch('billing/trialActivation')
        await this.$store.dispatch('organizations/read', this.$Organization?.id)
        await this.$store.dispatch('billing/getConsumptions')

        this.closeModal()

        if (
          this.step === this.stepsMap.documentCreate ||
          this.step === this.stepsMap.documentParticipants
        ) {
          this.$router.push({ name: 'documents-create' })
        } else if (this.step === this.stepsMap.createFolder) {
          this.$router.push({ name: 'documents-archive', query: { action: 'create-folder' } })
        } else if (this.step === this.stepsMap.contactsTable) {
          this.$router.push({ name: 'contacts' })
        }
        this.$setLocalStorageClientData({ showPremiumStepper: false })
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    countStepIndex(idx) {
      // Якщо trial Premium активований, то пропускаємо показ першого кроку, показуємо відразу фічі та фінальний крок
      return this.isTrialActivated ? idx + OUTRO_STEP : idx + INTRO_AND_OUTRO_STEPS
    }
  }
}
</script>

<style lang="scss">
.m-block-premium-features-stepper {
  &__features {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  &__features-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__item {
    display: grid;
    grid-template-columns: 48px 1fr;
    align-items: center;
    gap: 16px;

    &:not(:last-child) {
      margin-bottom: 24px;
    }
  }
  &__item-icon {
    width: 100%;
    height: 48px;
    border-radius: 50%;

    background-color: $background-gray;

    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 24px;
      height: auto;
    }
  }
  &__item-description {
    max-width: 460px;
  }
  &__image {
    width: 100%;
    height: 200px;
    background: linear-gradient(91deg, #fff -29.58%, #f8e3f4 66.15%);
    position: relative;

    display: flex;
    justify-content: center;
    align-items: flex-end;
  }
  &__step-chip {
    padding: 2px 8px;
    border-radius: 20px;
    background: $white;

    position: absolute;
    top: 26px;
    left: 40px;
    z-index: map-get($zIndexes, 'base');
  }
  &__stepper-content {
    padding: 0;
  }
  &__content {
    padding: 32px 40px 0 40px;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 68px 40px 40px 40px;

    &-left {
      display: flex;
      align-items: center;
      gap: 24px;
    }
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  &__progress-bar-wrapper {
    width: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
  }
}
</style>
